/**
 * Le planning : qui va où la semaine prochaine, et ce qui ne tient pas.
 *
 * Dernier bloc de la phase 3, et le seul qui regarde devant. `time_entries`
 * constate — il refuse une date future, exprès. `project_assignments` sait
 * déjà qui va où mais n'a jamais eu d'écran qui le lise en grille. Ce module
 * n'invente donc presque aucune donnée : il assemble celles qui existaient
 * déjà, et ajoute la seule qui manquait pour que l'assemblage ne mente pas —
 * les absences prévues.
 *
 * Aucune règle de cloisonnement ici. Elles vivent dans les policies posées par
 * 20260301002200, pour la raison habituelle : un porteur de JWT appelle
 * PostgREST sans passer par le moindre service. Les `allowedRoles` de ce
 * fichier rendent un refus lisible, ils ne le produisent pas.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { FieldRepository } from "../field/services.js";
import type { ProjectRepository } from "../projects/repository.js";
import { OPEN_PROJECT_STATUSES } from "../projects/types.js";
import type { TeamRepository } from "../team/services.js";
import { startOfWeek, weekDates } from "../team/types.js";
import { buildPlanningWeek } from "./types.js";
import type { Absence, AbsenceKind, PlanningWeek } from "./types.js";
import { computeAvailability } from "./availability.js";
import type { Availability } from "./availability.js";

/**
 * Trois mois de travail, examinés au plus.
 *
 * Au-delà, une date de début n'engage plus personne : le planning de décembre
 * n'existe pas en septembre, et l'annoncer à un client donnerait une précision
 * que la donnée n'a pas.
 */
const HORIZON_OUVRES = 60;

// ─── Entrées ───────────────────────────────────────────────────────────────

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ");

export const readPlanningInput = z.object({
  confirmationId: z.string().optional(),
  /** N'importe quel jour de la semaine voulue. */
  weekOf: isoDate,
  /** Réduire la grille à un salarié : sa semaine, seule. */
  employeeId: z.string().uuid().optional(),
});

export const listAbsencesInput = z.object({
  confirmationId: z.string().optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  employeeId: z.string().uuid().optional(),
});

export const planAbsenceInput = z
  .object({
    confirmationId: z.string().optional(),
    absenceId: z.string().uuid().optional(),
    employeeId: z.string().uuid(),
    kind: z
      .enum(["paid_leave", "unpaid_leave", "sick", "training", "other"])
      .default("paid_leave"),
    startsOn: isoDate,
    /**
     * Obligatoire, là où celle d'une affectation ne l'est pas. Une absence
     * sans fin n'est pas une absence, c'est un départ — et un départ s'écrit
     * sur la fiche du salarié.
     */
    endsOn: isoDate,
    notes: z.string().trim().max(500).nullish(),
  })
  .refine((v) => v.endsOn >= v.startsOn, {
    message: "La fin ne peut pas précéder le début",
    path: ["endsOn"],
  });

export const removeAbsenceInput = z.object({
  confirmationId: z.string().optional(),
  absenceId: z.string().uuid(),
});

/**
 * Ce qu'un salarié demande pour lui-même.
 *
 * Aucun `employeeId` : la base résout le demandeur depuis le compte connecté.
 * Accepter un identifiant ici permettrait de demander un congé au nom de
 * quelqu'un d'autre, et rien à l'écran ne le montrerait.
 */
export const requestAbsenceInput = z.object({
  confirmationId: z.string().optional(),
  kind: z
    .enum(["paid_leave", "unpaid_leave", "sick", "training", "other"])
    .default("paid_leave"),
  startsOn: isoDate,
  endsOn: isoDate,
  notes: z.string().trim().max(500).optional(),
});

export const withdrawAbsenceInput = z.object({
  confirmationId: z.string().optional(),
  absenceId: z.string().uuid(),
});

export const decideAbsenceInput = z.object({
  confirmationId: z.string().optional(),
  absenceId: z.string().uuid(),
  granted: z.boolean(),
  /** Un mot au demandeur. Facultatif pour un accord, précieux pour un refus. */
  note: z.string().trim().max(500).optional(),
});

/**
 * Ce qu'il faut pour proposer une date de début.
 *
 * `durationDays` et `crewSize` viennent de l'appelant et non d'une lecture :
 * la durée est sur le devis, l'effectif est un jugement du patron. Le planning
 * ne connaît ni l'un ni l'autre, et les deviner produirait une date fausse
 * qu'on annoncerait au client.
 */
export const projectAvailabilityInput = z.object({
  confirmationId: z.string().optional(),
  /** Premier jour envisageable. Aujourd'hui par défaut, côté appelant. */
  from: isoDate,
  durationDays: z.coerce.number().int().min(1).max(365).nullish(),
  crewSize: z.coerce.number().int().min(1).max(50).default(1),
});

export type ProjectAvailabilityInput = z.infer<typeof projectAvailabilityInput>;

export type RequestAbsenceInput = z.infer<typeof requestAbsenceInput>;
export type WithdrawAbsenceInput = z.infer<typeof withdrawAbsenceInput>;
export type DecideAbsenceInput = z.infer<typeof decideAbsenceInput>;

export type ReadPlanningInput = z.infer<typeof readPlanningInput>;
export type ListAbsencesInput = z.infer<typeof listAbsencesInput>;
export type PlanAbsenceInput = z.infer<typeof planAbsenceInput>;
export type RemoveAbsenceInput = z.infer<typeof removeAbsenceInput>;

// ─── Port ──────────────────────────────────────────────────────────────────

export interface SaveAbsenceCommand {
  absenceId?: string;
  employeeId: string;
  kind: AbsenceKind;
  startsOn: string;
  endsOn: string;
  notes: string | null;
}

export interface AbsenceRepository {
  listAbsences(
    ctx: ExecutionContext,
    filter: { from?: string; to?: string; employeeId?: string },
  ): Promise<Absence[]>;
  saveAbsence(ctx: ExecutionContext, cmd: SaveAbsenceCommand): Promise<Absence>;
  removeAbsence(ctx: ExecutionContext, absenceId: string): Promise<void>;

  /**
   * Dépose une demande au nom de l'appelant.
   *
   * Passe par `request_absence()` en base : la policy `managers_write` réserve
   * l'écriture au bureau, et c'est juste — un ouvrier ne pose pas une absence,
   * il en demande une.
   */
  requestAbsence(
    ctx: ExecutionContext,
    cmd: { kind: AbsenceKind; startsOn: string; endsOn: string; notes: string | null },
  ): Promise<string>;

  /** Retire une demande de l'appelant à laquelle personne n'a répondu. */
  withdrawRequest(ctx: ExecutionContext, absenceId: string): Promise<void>;

  /**
   * Les absences du salarié correspondant au compte appelant, et elles seules.
   *
   * Passe par `my_absences()` : l'écran ne connaît pas son `employee_id`, et
   * la policy `read_own_or_manage` laisse le bureau lire toute l'entreprise —
   * ce qui est juste pour le planning, et faux sous un intitulé « les miennes ».
   */
  myAbsences(ctx: ExecutionContext): Promise<Absence[]>;

  /** Accorde ou refuse. Les RLS décident qui en a le droit. */
  decideAbsence(
    ctx: ExecutionContext,
    cmd: { absenceId: string; granted: boolean; note: string | null },
  ): Promise<Absence>;
}

/**
 * Le planning ne possède presque rien : il lit l'équipe, les affectations et
 * les chantiers là où ils vivent déjà. Composer plutôt que recopier — quatre
 * dépôts, une seule vérité par donnée.
 */
export interface PlanningDeps {
  absences: AbsenceRepository;
  team: Pick<TeamRepository, "listEmployees">;
  field: Pick<FieldRepository, "listAssignments">;
  projects: Pick<ProjectRepository, "list">;
}

// ─── Services ──────────────────────────────────────────────────────────────

export function createPlanningServices(deps: PlanningDeps) {
  const lirePlanning = defineService<ReadPlanningInput, PlanningWeek>({
    name: "lire_planning",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const weekStart = startOfWeek(input.weekOf);
      const dates = weekDates(weekStart);
      const weekEnd = dates[6] ?? weekStart;
      const employeeId = input.employeeId;

      const [employees, assignments, absences, projects] = await Promise.all([
        // Les salariés partis sont exclus : ils n'ont pas de semaine à venir.
        deps.team.listEmployees(ctx, false),
        deps.field.listAssignments(ctx, {
          from: weekStart,
          to: weekEnd,
          ...(employeeId ? { employeeId } : {}),
        }),
        deps.absences.listAbsences(ctx, {
          from: weekStart,
          to: weekEnd,
          ...(employeeId ? { employeeId } : {}),
        }),
        // Tous les chantiers ouverts, pas seulement ceux qui ont du monde :
        // c'est justement l'absence d'affectation qu'on cherche.
        deps.projects.list(ctx, { status: [...OPEN_PROJECT_STATUSES] }),
      ]);

      const retenus = employeeId ? employees.filter((e) => e.id === employeeId) : employees;

      return buildPlanningWeek({
        weekStart,
        employees: retenus,
        assignments,
        absences,
        // Réduire la grille à un salarié ne veut pas dire lui reprocher les
        // chantiers vides de toute l'entreprise : la question posée est « sa
        // semaine », pas « la couverture générale ».
        projects: employeeId ? [] : projects,
      });
    },
  });

  const listerAbsences = defineService<ListAbsencesInput, Absence[]>({
    name: "lister_absences",
    level: ActionLevel.Read,
    handler: (ctx, input) =>
      deps.absences.listAbsences(ctx, {
        ...(input.from ? { from: input.from } : {}),
        ...(input.to ? { to: input.to } : {}),
        ...(input.employeeId ? { employeeId: input.employeeId } : {}),
      }),
  });

  const poserAbsence = defineService<PlanAbsenceInput, Absence>({
    name: "poser_absence",
    // Réversible : une absence mal saisie se corrige ou se retire, et rien
    // n'est parti à l'extérieur. Aucun solde ne bouge — il n'y en a pas.
    level: ActionLevel.ReversibleWrite,
    // On ne se met pas en congé tout seul. La base l'impose aussi, par la
    // policy `managers_write`.
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      deps.absences.saveAbsence(ctx, {
        ...(input.absenceId ? { absenceId: input.absenceId } : {}),
        employeeId: input.employeeId,
        kind: input.kind,
        startsOn: input.startsOn,
        endsOn: input.endsOn,
        notes: input.notes ?? null,
      }),
  });

  const retirerAbsence = defineService<RemoveAbsenceInput, void>({
    name: "retirer_absence",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) => deps.absences.removeAbsence(ctx, input.absenceId),
  });

  /**
   * Demander un congé.
   *
   * Aucun `allowedRoles`, et c'est tout l'objet du verbe : celui qui demande
   * est précisément celui qui n'a pas le droit de poser. Le patron peut le
   * faire aussi — il se demande alors à lui-même, ce qui est inutile mais pas
   * dangereux, et lui interdire coûterait une règle pour rien.
   *
   * `ReversibleWrite` : rien ne part à l'extérieur, et une demande se retire
   * tant qu'elle n'a pas de réponse.
   */
  const demanderAbsence = defineService<RequestAbsenceInput, { absenceId: string }>({
    name: "demander_absence",
    level: ActionLevel.ReversibleWrite,
    handler: async (ctx, input) => ({
      absenceId: await deps.absences.requestAbsence(ctx, {
        kind: input.kind,
        startsOn: input.startsOn,
        endsOn: input.endsOn,
        notes: input.notes ?? null,
      }),
    }),
  });

  /**
   * Mes absences à moi.
   *
   * Aucun rôle exigé et aucun argument : la base répond « les tiennes » à
   * quiconque demande. Un compte sans fiche de salarié obtient une liste vide.
   */
  const mesAbsences = defineService<Record<string, never>, Absence[]>({
    name: "mes_absences",
    level: ActionLevel.Read,
    handler: (ctx) => deps.absences.myAbsences(ctx),
  });

  const retirerMaDemande = defineService<WithdrawAbsenceInput, void>({
    name: "retirer_ma_demande_absence",
    level: ActionLevel.ReversibleWrite,
    handler: (ctx, input) => deps.absences.withdrawRequest(ctx, input.absenceId),
  });

  /**
   * Répondre à une demande.
   *
   * Les mêmes rôles que `poser_absence` : accorder un congé revient à en poser
   * un, et vider une case du planning engage l'organisation de la semaine.
   */
  const repondreDemandeAbsence = defineService<DecideAbsenceInput, Absence>({
    name: "repondre_demande_absence",
    level: ActionLevel.ReversibleWrite,
    allowedRoles: ["patron", "conducteur", "assistant"],
    handler: (ctx, input) =>
      deps.absences.decideAbsence(ctx, {
        absenceId: input.absenceId,
        granted: input.granted,
        note: input.note ?? null,
      }),
  });

  /**
   * À partir de quand ce chantier peut commencer.
   *
   * Lecture seule et composée, comme `lire_planning` : l'équipe, les
   * affectations et les absences là où elles vivent déjà. La fenêtre
   * interrogée couvre l'horizon en jours **calendaires** — cinq jours ouvrés
   * en occupent sept — plus une marge, sans quoi une affectation qui commence
   * juste après la borne resterait invisible et la date proposée serait trop
   * optimiste.
   */
  const disponibiliteChantier = defineService<ProjectAvailabilityInput, Availability>({
    name: "disponibilite_chantier",
    level: ActionLevel.Read,
    handler: async (ctx, input) => {
      const horizonOuvres = HORIZON_OUVRES;
      // La fenêtre lue doit couvrir l'horizon **plus la durée du chantier** :
      // `computeAvailability` examine des créneaux qui dépassent l'horizon, et
      // un jour sans donnée d'affectation passerait pour libre. On aurait
      // promis au client un créneau déjà pris — le défaut le plus coûteux que
      // ce calcul pouvait produire.
      const ouvresALire = horizonOuvres + (input.durationDays ?? 1);
      const fin = new Date(
        Date.parse(`${input.from}T00:00:00Z`) + Math.ceil(ouvresALire * 1.4 + 14) * 86_400_000,
      )
        .toISOString()
        .slice(0, 10);

      const [employees, assignments, absences] = await Promise.all([
        deps.team.listEmployees(ctx, false),
        deps.field.listAssignments(ctx, { from: input.from, to: fin }),
        deps.absences.listAbsences(ctx, { from: input.from, to: fin }),
      ]);

      return computeAvailability({
        from: input.from,
        employees,
        assignments,
        absences,
        durationDays: input.durationDays ?? null,
        crewSize: input.crewSize,
        horizonDays: horizonOuvres,
      });
    },
  });

  return {
    lirePlanning,
    disponibiliteChantier,
    listerAbsences,
    poserAbsence,
    retirerAbsence,
    demanderAbsence,
    mesAbsences,
    retirerMaDemande,
    repondreDemandeAbsence,
  };
}

export type PlanningServices = ReturnType<typeof createPlanningServices>;
